import { Injectable, inject } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { VALIDATOR_REGEX_EMAIL } from '@mavles/shared';
import { IAddress, IContact } from '@models/contacts/hrm/contacts';
import { Address } from '@models/contacts/customer/Customer';

@Injectable({ providedIn: 'root' })
export class ContactService {
  private fb = inject(FormBuilder);

  buildContactForm(contact: IContact): FormGroup {
    return this.fb.group({
      contact_id: [contact.contact_id],

      image: [''],
      image_id: [contact.image_id],

      name: [contact.name, Validators.required],

      phone: [contact.phone, [Validators.pattern('^[6-9][0-9]{9}$')]],

      email: [
        contact.email,
        [Validators.pattern(VALIDATOR_REGEX_EMAIL), Validators.email],
      ],

      gst: [
        contact.gst,
        [
          Validators.pattern(
            '^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$',
          ),
        ],
      ],

      notes: [contact.notes],

      status: [contact.status],
      due_listing: [contact.due_listing],

      contact_type_id: [{ value: contact.contact_type_id, disabled: true }, Validators.required],
      contact_type_name: [''],

      address: this.fb.array([]),

      contact_group_ids: [contact.contact_group_ids],
    });
  }

  buildAddressForm(address: IAddress): FormGroup {
    return this.fb.group({
      address_id: [address.address_id],

      address_type: [address.address_type, Validators.required],
      address_city: [address.address_city, Validators.required],

      address_street: [address.address_street],
      address_street_2: [address.address_street_2],

      address_state: [address.address_state],

      address_pin_code: [
        address.address_pin_code,
        [Validators.pattern('^[0-9]{6}$')],
      ],

      address_delete: [false],
    });
  }

  getAddressArray(form: FormGroup): FormArray {
    return form.get('address') as FormArray;
  }

  addAddress(form: FormGroup, address?: IAddress): void {
    this.getAddressArray(form).push(
      this.buildAddressForm(address ?? new Address()),
    );
  }

  populateAddresses(form: FormGroup, addresses: IAddress[]): void {
    addresses.forEach((a) => this.addAddress(form, a));
  }

  removeAddress(form: FormGroup, index: number): void {
    this.getAddressArray(form).removeAt(index);
  }

  onAddressRemove(form: FormGroup, index: number): void {
    const arr = this.getAddressArray(form);

    if (arr.at(index).get('address_id')?.value) {
      arr.at(index).get('address_delete')?.setValue(true);
    } else {
      arr.removeAt(index);
    }
  }

  /** Returns a CSS class based on whether the address row is persisted or new. */
  getAddressClass(form: FormGroup, index: number): string {
    const arr = this.getAddressArray(form);
    return arr.at(index).get('address_id')?.value
      ? 'address-shadow-success'
      : 'address-shadow-danger';
  }

  /**
   * Inspects the contact form and returns a list of human-readable error
   * messages covering all broken validation rules (contact + each address row).
   * Returns an empty array when the form is valid.
   */
  getValidationErrors(form: FormGroup): string[] {
    const errors: string[] = [];

    // ── Contact fields ────────────────────────────────────────────────────────
    const name = form.get('name');
    if (name?.hasError('required')) {
      errors.push('Contact Name is required.');
    }

    const phone = form.get('phone');
    if (phone?.hasError('pattern')) {
      errors.push(
        'Phone must be a valid 10-digit Indian mobile number (starts with 6-9).',
      );
    }

    const email = form.get('email');
    if (email?.hasError('pattern') || email?.hasError('email')) {
      errors.push('Email address is not valid.');
    }

    const gst = form.get('gst');
    if (gst?.hasError('pattern')) {
      errors.push('GST number format is invalid (e.g. 29ABCDE1234F1Z5).');
    }

    // ── Address rows ─────────────────────────────────────────────────────────
    const addressArray = this.getAddressArray(form);
    addressArray.controls.forEach((ctrl, i) => {
      const addrGroup = ctrl as FormGroup;
      const rowNum = i + 1;

      if (addrGroup.get('address_type')?.hasError('required')) {
        errors.push(`Address ${rowNum}: Type is required.`);
      }
      if (addrGroup.get('address_city')?.hasError('required')) {
        errors.push(`Address ${rowNum}: City is required.`);
      }
      if (addrGroup.get('address_pin_code')?.hasError('pattern')) {
        errors.push(`Address ${rowNum}: PIN Code must be exactly 6 digits.`);
      }
    });

    return errors;
  }
}
